import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, UserPlus, Calendar } from "lucide-react";

const currentMonthIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function UnmatchedGrid({ onAssign }) {
  const [month, setMonth] = useState(currentMonthIso());
  const [data, setData] = useState({ days: [], rows: [], total_unmatched_punch_ids: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/biometric/unmatched-grid", { params: { month } });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load unmatched grid");
      setData({ days: [], rows: [], total_unmatched_punch_ids: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const filteredRows = data.rows.filter(r =>
    !search || String(r.punch_id).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="unmatched-grid" className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-md p-3">
        <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded px-2 py-1">
          <Calendar className="h-3.5 w-3.5 text-slate-500" />
          <input type="month" value={month}
                 onChange={(e) => setMonth(e.target.value || currentMonthIso())}
                 className="text-xs bg-transparent outline-none"
                 data-testid="unmatched-grid-month" />
        </div>
        <Input placeholder="Filter by punch ID..." value={search}
               onChange={(e) => setSearch(e.target.value)}
               className="h-8 text-xs max-w-xs"
               data-testid="unmatched-grid-search" />
        <span className="text-xs text-slate-500">
          {filteredRows.length} of {data.total_unmatched_punch_ids} unmatched punch IDs
        </span>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                className="ml-auto" data-testid="unmatched-grid-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {!loading && filteredRows.length === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded p-6 text-center text-sm text-slate-500">
          {data.total_unmatched_punch_ids === 0
            ? `No unmatched punches recorded in ${month}. 🎉`
            : "No rows match your filter."}
        </div>
      )}

      {/* Grid */}
      {filteredRows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: "75vh" }}>
            <table className="text-xs border-collapse">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left sticky left-0 bg-slate-100 z-20 w-44">Punch ID</th>
                  <th className="px-2 py-2 text-left bg-slate-100 w-20">Total</th>
                  <th className="px-2 py-2 text-left bg-slate-100">Field</th>
                  {data.days.map(d => (
                    <th key={d.day}
                        className={`px-1 py-2 text-center w-14 border-l border-slate-200 ${
                          d.is_sunday ? "bg-amber-100" : "bg-slate-100"
                        }`}>
                      <div className="flex flex-col items-center">
                        <span className="text-sm">{d.day}</span>
                        <span className={`text-[9px] uppercase ${
                          d.is_sunday ? "text-amber-800 font-semibold" : "text-slate-500"
                        }`}>{d.weekday}</span>
                      </div>
                    </th>
                  ))}
                  <th className="px-2 py-2 text-center bg-slate-100 w-24">Action</th>
                </tr>
              </thead>
              {filteredRows.map(row => {
                const fields = [
                  { key: "first_in", label: "First IN" },
                  { key: "last_out", label: "Last Out" },
                ];
                return (
                  <tbody key={row.punch_id} className="border-t-2 border-slate-300">
                    {fields.map((f, fi) => (
                      <tr key={`${row.punch_id}-${f.key}`} className="border-b border-slate-100">
                        {fi === 0 && (
                          <>
                            <td rowSpan={2}
                                className="px-2 py-1 sticky left-0 bg-white border-r border-slate-200 align-top z-10 w-44">
                              <div className="text-sm font-bold text-slate-900 font-mono">{row.punch_id}</div>
                              <div className="text-[10px] text-slate-500">
                                {row.earliest_date?.slice(5)} → {row.latest_date?.slice(5)}
                              </div>
                            </td>
                            <td rowSpan={2}
                                className="px-2 py-1 align-top text-[11px] text-slate-700">
                              <div><b>{row.total_days}</b> days</div>
                              <div className="text-slate-500">{row.total_punches} punches</div>
                            </td>
                          </>
                        )}
                        <td className="px-2 py-1 bg-slate-50 text-[10px] text-slate-600 font-medium uppercase tracking-wider whitespace-nowrap">
                          {f.label}
                        </td>
                        {data.days.map(d => {
                          const day = row.days[d.day];
                          const sundayBg = d.is_sunday ? "bg-amber-50/60" : "";
                          if (!day) return <td key={d.day}
                                               className={`px-1 py-1 text-center border-l border-slate-100 text-slate-300 ${sundayBg}`}>—</td>;
                          const val = f.key === "first_in" ? day.first_in : day.last_out;
                          const isSamePunch = day.first_in === day.last_out;
                          return (
                            <td key={d.day}
                                className={`px-1 py-1 text-center border-l border-slate-100 font-mono ${sundayBg} ${
                                  f.key === "first_in"
                                    ? "bg-emerald-50/40 text-emerald-800"
                                    : isSamePunch ? "text-slate-300" : "text-slate-700"
                                }`}
                                title={day.punch_count ? `${day.punch_count} punch(es)` : ""}>
                              {f.key === "last_out" && isSamePunch ? "" : val}
                            </td>
                          );
                        })}
                        {fi === 0 && (
                          <td rowSpan={2} className="text-center align-middle border-l border-slate-100">
                            <Button size="sm"
                                    onClick={() => onAssign && onAssign(row)}
                                    className="brand-bg brand-bg-hover text-white h-7 text-[11px]"
                                    data-testid={`grid-assign-${row.punch_id}`}>
                              <UserPlus className="h-3 w-3 mr-1" /> Assign
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                );
              })}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
